<script setup lang="ts">
import { useProductCounter } from '@/composables/useProductCounter'

import { useAppStore } from '@/store/app/app'
import { SnackbarType } from '@/store/app/app.types'
import { useBasketStore } from '@/store/basket/basket'
import { useCityStore } from '@/store/city/city'

import { NewProduct } from '@/types/api/api-v4/product'
import {
  ProductParentType,
  ProductType,
} from '@/types/components/product-card.types'
import { EcommerceEventName } from '@/types/plugins/yandex-metrika'

import ProductCardContent from '@/components/Product/ProductCard/ProductCardContent.vue'
import ProductCardFooter from '@/components/Product/ProductCard/ProductCardFooter.vue'
import ProductCardImage from '@/components/Product/ProductCard/ProductCardImage.vue'
import ProductTags from '@/components/Product/ProductTags/ProductTags.vue'

import { mapEcommerceData } from '@/services/metrics/utils/ecommerce'

type Props = {
  product: NewProduct
  isLoading?: boolean
}

const props = defineProps<Props>()

const emit = defineEmits(['on-open-dialog'])

const { $metrics } = useNuxtApp()

const { currencyCode } = useCityStore()
const { showSnackbar } = useAppStore()
const basketStore = useBasketStore()

const productId = computed(() => currentModification.value.id)

// Находим модификацию с наименьшей ценой
const currentModification = computed(() => {
  return props.product.modifications.reduce((acc, modification) => {
    return acc.price < modification.price ? acc : modification
  })
})

const productBasketQuantity = computed(() => {
  const inBasket = basketStore.isProductInBasket(productId.value)

  return inBasket?.quantity || 0
})

const productType = computed(() => {
  if (props.product.modifications.length > 1) return ProductType.selection

  return ProductType.simple
})

const {
  basketError,
  quantitySelectedProducts,
  totalSelectedProductsQuantity,
  setProductQuantity,
  setTotalProductsQuantity,
  decreaseQuantity,
  increaseQuantity,
} = useProductCounter(productId, currentModification.value, props.product)

const { pushMetrics } = useSendMetricsOfProduct(currentModification.value)

const onOpenDialog = () => {
  emit('on-open-dialog', props.product)

  const ecommerceData = mapEcommerceData(
    currentModification.value,
    EcommerceEventName.DETAIL,
    currencyCode?.value || '',
  )

  $metrics?.eventViewedProduct({ ym: ecommerceData })
}

const addToCart = async () => {
  if (productType.value === ProductType.selection) {
    onOpenDialog()
    return
  }

  setProductQuantity(quantitySelectedProducts.value + 1) // текущее + 1

  await basketStore.addProduct(productId.value)

  if (!basketError.value) {
    pushMetrics(EcommerceEventName.ADD, 1)

    return
  }

  setProductQuantity(productBasketQuantity.value) // при ошибке возвращаем кол-во с сервера

  showSnackbar({
    message: 'Что-то пошло не так',
    type: SnackbarType.MESSAGE,
  })
}

watch(
  () => basketStore.isLoaded,
  (state) => state && setTotalProductsQuantity(),
)

onMounted(() => {
  if (basketStore.isLoaded) setTotalProductsQuantity()
})
</script>

<template>
  <div class="product-card" :class="{ 'product-card--loading': isLoading }">
    <product-card-image
      :product="product"
      :current-modification="currentModification"
      :products-quantity="totalSelectedProductsQuantity"
      :is-loading="isLoading"
      @on-open-dialog="onOpenDialog"
    />

    <a :href="`/product/${product.slug}/`" @click.prevent="onOpenDialog">
      <product-tags
        :type="ProductParentType.card"
        :current-modification="currentModification"
        :main-tags="product.main_tags"
        :additional-tags="product.additional_tags"
      />
    </a>

    <product-card-content
      :product="product"
      :product-type="productType"
      :current-modification="currentModification"
      @on-open-dialog="onOpenDialog"
    />

    <product-card-footer
      :product="product"
      :product-type="productType"
      :current-modification="currentModification"
      :products-quantity="quantitySelectedProducts"
      @on-add="addToCart"
      @on-increase="increaseQuantity"
      @on-decrease="decreaseQuantity"
    />
  </div>
</template>

<style lang="scss" scoped>
.product-card {
  position: relative;
  display: flex;
  flex-direction: column;
  height: 248px;
  overflow: hidden;
  background-color: var(--ui-grey);
  border-radius: 16px;
  transition: background-color 0.2s ease-in-out;

  @include media('>=desktop-mini') {
    height: 392px;
    border-radius: 25px;

    &:hover {
      background-color: var(--ui-grey--hover);
    }
  }

  &--loading {
    &::after {
      position: absolute;
      top: 0;
      right: 0;
      bottom: 0;
      left: 0;
      z-index: 3;
      content: '';
      background: linear-gradient(
        122.57deg,
        #f1f3f9 34.56%,
        #ffffff 51.72%,
        #f1f4f9 79.78%
      );
      background-size: 1200px 100%;
      animation: shimmer 3s linear infinite forwards;
    }
  }
}

@keyframes shimmer {
  0% {
    background-position: -1200px 0;
  }
  100% {
    background-position: 1200px 0;
  }
}
</style>
